import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PlayerService } from 'src/app/services/player.service';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '@env/environment';

@Component({
  selector: 'app-player',
  templateUrl: './player.component.html',
  styleUrls: ['./player.component.scss']
})
export class PlayerComponent implements OnInit, OnDestroy {

  @ViewChild('playerIframe') playerIframe: ElementRef<HTMLIFrameElement>;
  @ViewChild('iframeContainer') iframeContainer: ElementRef<HTMLDivElement>;

  iframeUrl: SafeResourceUrl | null = null;
  // Marca cuando el iframe terminó de cargar (oculta el overlay de carga)
  iframeLoaded = false;
  private messageListener: (event: MessageEvent) => void;
  // Handles de los reintentos del envío de token (para poder cancelarlos).
  private authRetryHandles: number[] = [];

  constructor(
    private playerService: PlayerService,
    private sanitizer: DomSanitizer,
    private cookieService: CookieService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.playerService.iframeUrl$.subscribe((url) => {
      this.iframeUrl = url ? this.sanitizeUrl(url) : null;
      // Al cerrar el iframe reseteamos estado y cancelamos reintentos pendientes.
      if (!url) {
        this.iframeLoaded = false;
        this.clearAuthRetries();
      }
      // Forzar detección de cambios para que el *ngIf renderice el iframe
      // inmediatamente, antes de que el player pueda enviar player_ready.
      // Sin esto, el @ViewChild puede ser null cuando llega el primer mensaje.
      this.cdr.detectChanges();
    });

    // Escuchar señal player_ready: el player avisa cuando Angular terminó de inicializar.
    // Validamos que el emisor sea exactamente el iframe que cargamos (event.source),
    // en lugar de comparar el origin — esto soporta dominios de tenant personalizados.
    this.messageListener = (event: MessageEvent) => {
      console.log('[APP PlayerComponent] message recibido - origin:', event.origin, 'type:', event.data?.type);
      const iframeWindow = this.playerIframe?.nativeElement?.contentWindow ?? null;
      // Si el ViewChild ya resolvió, validamos por la ventana del iframe. Si aún no
      // resolvió (carrera del PRIMER render), aceptamos el mensaje siempre que haya
      // un iframe abierto: el handshake `player_ready` solo lo emite nuestro player.
      // Así no se descarta el primer `player_ready` y el token siempre se envía.
      const fromPlayer = iframeWindow ? event.source === iframeWindow : !!this.iframeUrl;

      if (event.data?.type === 'player_ready' && fromPlayer) {
        console.log('[APP PlayerComponent] player_ready recibido desde iframe. Origin real:', event.origin);
        this.sendAuthToken(event.source as Window, event.origin);
      }

      // El curso reporta su altura real: ajustamos el alto del iframe para que el
      // contenedor del padre sea el que scrollee (barra visible al borde de la ventana).
      if (event.data?.type === 'player_height' && fromPlayer) {
        const height = Number(event.data.height);
        if (height > 0 && this.playerIframe?.nativeElement) {
          this.playerIframe.nativeElement.style.height = height + 'px';
        }
      }

      // El curso reenvía el wheel que ocurre sobre su contenido (no se propaga solo
      // por ser cross-origin); desplazamos el contenedor del padre.
      if (event.data?.type === 'player_wheel' && fromPlayer) {
        const factor = event.data.deltaMode === 1
          ? 16
          : event.data.deltaMode === 2
            ? window.innerHeight
            : 1;
        this.iframeContainer?.nativeElement?.scrollBy({
          left: (event.data.deltaX || 0) * factor,
          top: (event.data.deltaY || 0) * factor,
          behavior: 'auto'
        });
      }
    };
    window.addEventListener('message', this.messageListener);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageListener);
    this.clearAuthRetries();
  }

  private sendAuthToken(target: Window, targetOrigin: string): void {
    const token = this.cookieService.get(environment.tokenName);
    const refreshToken = this.cookieService.get(environment.refreshTokenName);
    const currentCompany = this.cookieService.get('current_company');
    console.log('[APP PlayerComponent] sendAuthToken - token exists:', !!token, 'currentCompany:', currentCompany, 'targetOrigin:', targetOrigin);
    if (!token) {
      console.warn('[APP PlayerComponent] sendAuthToken - NO HAY TOKEN, no se envía postMessage');
      return;
    }

    const message = {
      type: 'auth_token',
      access_token: token,
      refresh_token: refreshToken,
      current_company: currentCompany
    };

    // Envío inmediato + reintentos idempotentes: si el player emitió `player_ready`
    // antes de tener listo su listener de `auth_token`, el envío único se perdía y
    // el contenido no cargaba (fallaba "la primera vez"). Reenviar al mismo
    // source/origin es seguro (el player solo se re-autentica) y elimina esa carrera.
    this.clearAuthRetries();
    target.postMessage(message, targetOrigin);
    console.log('[APP PlayerComponent] postMessage enviado');

    [400, 1000, 2000].forEach((delay) => {
      const handle = window.setTimeout(() => {
        // Solo reenvía si el iframe sigue abierto.
        if (this.iframeUrl) {
          target.postMessage(message, targetOrigin);
          console.log('[APP PlayerComponent] postMessage reenviado (retry ' + delay + 'ms)');
        }
      }, delay);
      this.authRetryHandles.push(handle);
    });
  }

  private clearAuthRetries(): void {
    this.authRetryHandles.forEach((handle) => window.clearTimeout(handle));
    this.authRetryHandles = [];
  }

  sanitizeUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  // Al cargar el iframe, marca iframeLoaded para desvanecer el overlay de carga
  onIframeLoad(): void {
    this.iframeLoaded = true;
  }
}
